import { Dialog, Icon } from "@uigovpe/components";
import { type ChangeEvent, useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import type { ProductFormValues, ProductImage } from "../types";
import ImagePreview from "./image-preview";
import { useImageSource } from "@/hooks/images/useImageSource";

const MAX_GALLERY = 4;
const acceptedTypes = ["image/jpeg", "image/png", "image/webp"];

function validate(file: File) {
  if (!acceptedTypes.includes(file.type))
    return "Use uma imagem PNG, JPG ou WebP.";
  if (file.size > 5 * 1024 * 1024) return "A imagem deve ter até 5MB.";
  return null;
}

function createImage(file: File): Promise<ProductImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result !== "string") {
        reject(new Error("Não foi possível visualizar a imagem."));
        return;
      }
      resolve({
        file,
        id: crypto.randomUUID?.() ?? `${file.name}-${file.lastModified}`,
        name: file.name,
        url: reader.result,
      });
    });
    reader.addEventListener("error", () =>
      reject(new Error("Não foi possível visualizar a imagem.")),
    );
    reader.readAsDataURL(file);
  });
}

export default function ProductImages() {
  const {
    clearErrors,
    control,
    formState: { errors },
    setError,
    setValue,
  } = useFormContext<ProductFormValues>();
  const { field: mainField } = useController({
    name: "mainImage",
    control,
    rules: {
      validate: (image) => Boolean(image) || "Adicione a foto principal.",
    },
  });
  const { field: galleryField } = useController({
    name: "galleryImages",
    control,
  });
  const [preview, setPreview] = useState<ProductImage | null>(null);
  const { imageSource: previewSource } = useImageSource(preview?.url);
  const main = mainField.value;
  const gallery = galleryField.value;
  const emptySlots = useMemo(
    () => Math.max(MAX_GALLERY - gallery.length, 0),
    [gallery.length],
  );

  const uploadMain = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const error = validate(file);
      if (error) setError("mainImage", { message: error });
      else {
        try {
          setValue("mainImage", await createImage(file), {
            shouldDirty: true,
            shouldValidate: true,
          });
          clearErrors("mainImage");
        } catch (previewError) {
          setError("mainImage", {
            message:
              previewError instanceof Error
                ? previewError.message
                : "Não foi possível visualizar a imagem.",
          });
        }
      }
    }
    event.target.value = "";
  };
  const uploadGallery = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []).slice(
      0,
      MAX_GALLERY - gallery.length,
    );
    const error = files.map(validate).find(Boolean);
    if (error) setError("galleryImages", { message: error });
    else if (files.length) {
      try {
        const newImages = await Promise.all(files.map(createImage));
        setValue("galleryImages", [...gallery, ...newImages], {
          shouldDirty: true,
          shouldValidate: true,
        });
        clearErrors("galleryImages");
      } catch (previewError) {
        setError("galleryImages", {
          message:
            previewError instanceof Error
              ? previewError.message
              : "Não foi possível visualizar as imagens.",
        });
      }
    }
    event.target.value = "";
  };

  return (
    <section className="flex! flex-col! gap-5!">
      <div>
        <div className="flex! items-center! gap-2! text-base! font-bold! text-zinc-950!">
          <Icon icon="photo_camera" outline className="text-[#4e3873]!" />
          <h2 className="m-0!">Imagem</h2>
        </div>
        <p className="mt-2! text-sm! text-[#28272c]!">
          Adicione fotos que representem bem seu produto
        </p>
      </div>
      <div className="flex! flex-col! gap-3!">
        <span className="text-sm! font-medium! text-zinc-950!">
          Foto principal *
        </span>
        {main ? (
          <ImagePreview
            image={main}
            isMain
            onView={() => setPreview(main)}
            onRemove={() => {
              setValue("mainImage", null, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          />
        ) : (
          <label className="flex! h-72! cursor-pointer! flex-col! items-center! justify-center! rounded-sm! border! border-dashed! border-violet-light! bg-white/40! px-6! py-11! text-center! hover:bg-violet-ultralight/40! md:h-80!">
            <input
              type="file"
              accept={acceptedTypes.join(",")}
              className="hidden!"
              onChange={uploadMain}
            />
            <span className="mb-4! flex! size-10! items-center! justify-center! rounded-full! bg-zinc-200! text-[#4e3873]!">
              <Icon icon="image" outline />
            </span>
            <span className="text-sm!">
              Clique para adicionar a foto principal, prefira imagens nítidas e
              bem iluminadas
            </span>
            <span className="mt-1! text-sm! text-[#52525b]!">
              Formato paisagem recomendado. PNG, JPG ou WebP até 5MB.
            </span>
          </label>
        )}
        {errors.mainImage?.message && (
          <span className="text-sm! text-red-700!">
            {errors.mainImage.message}
          </span>
        )}
      </div>
      <div className="flex! flex-col! gap-2!">
        <span className="text-sm! font-medium!">
          Galeria de fotos (opcional)
        </span>
        <div className="flex! flex-wrap! gap-3!">
          {gallery.length < MAX_GALLERY && (
            <label className="flex! size-20! cursor-pointer! items-center! justify-center! rounded-sm! border! border-dashed! border-violet-light! bg-white! text-[#4e3873]!">
              <input
                type="file"
                accept={acceptedTypes.join(",")}
                multiple
                className="hidden!"
                onChange={uploadGallery}
              />
              <Icon icon="add" />
            </label>
          )}
          {gallery.map((image) => (
            <ImagePreview
              key={image.id}
              image={image}
              onView={() => setPreview(image)}
              onRemove={() => {
                setValue(
                  "galleryImages",
                  gallery.filter((item) => item.id !== image.id),
                  { shouldDirty: true },
                );
              }}
            />
          ))}
          {Array.from({ length: emptySlots }).map((_, index) => (
            <div
              key={index}
              className="flex! size-20! items-center! justify-center! rounded-md! bg-zinc-200! text-violet-light!"
              aria-hidden="true"
            >
              <Icon icon="image" outline />
            </div>
          ))}
        </div>
        {errors.galleryImages?.message && (
          <span className="text-sm! text-red-700!">
            {errors.galleryImages.message}
          </span>
        )}
      </div>
      <Dialog
        visible={Boolean(preview)}
        onHide={() => setPreview(null)}
        header={preview?.name ?? "Visualizar foto"}
        className="w-[min(92vw,720px)]!"
        draggable={false}
      >
        {preview && previewSource && (
          <div className="h-[min(70vh,520px)]! w-full! overflow-hidden! rounded-sm! bg-zinc-100!">
            <img
              src={previewSource}
              alt={preview.name}
              className="h-full! w-full! object-contain!"
            />
          </div>
        )}
      </Dialog>
    </section>
  );
}
